import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { offersResponseSchema } from '@rd/schemas/offer.schema';
import {
  FormattedOfferData,
  FormattedPaginatedOfferData,
  Offer,
  OfferQueryParams,
  OfferSource,
} from '@rd/types';
import { OffersResponse } from '@rd/types/offer.response';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiValidationService } from '../api-validation/api-validation.service';
import { NydusNetworkApiDataService } from '../nydus-network-api-data/nydus-network-api-data.service';

@Injectable({
  providedIn: 'root',
})
export class OfferApiService {
  private nydusNetworkDataService = inject(NydusNetworkApiDataService);
  private apiValidationService = inject(ApiValidationService);

  getOffers(queryParams?: OfferQueryParams): Observable<FormattedOfferData> {
    const offersParams = this.buildParams(queryParams);
    return this.nydusNetworkDataService
      .get<OffersResponse>('offers', offersParams)
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<OffersResponse | null>({
            apiName: `offers?${decodeURIComponent(offersParams.toString())}`,
            schema: offersResponseSchema,
            response,
          });
        }),
        map((offersResponse) => {
          return this.transformOfferResponse(
            offersResponse,
            queryParams?.source,
          );
        }),
      );
  }

  getOffersPaginated({
    page,
    pageSize,
    queryParams,
  }: {
    page: number;
    pageSize: number;
    queryParams?: OfferQueryParams;
  }): Observable<FormattedPaginatedOfferData> {
    const offersParams = this.buildParams(queryParams);
    return this.nydusNetworkDataService
      .getPaginated<OffersResponse>('offers', page, pageSize, offersParams)
      .pipe(
        tap((paginatedOffersResponse) => {
          this.apiValidationService.validate<OffersResponse | null>({
            apiName: `offers?page[number]=${page}&page[size]=${pageSize}&${decodeURIComponent(
              offersParams.toString(),
            )}`,
            schema: offersResponseSchema,
            response: paginatedOffersResponse.body,
          });
        }),
        map((paginatedOffersResponse) => {
          return this.transformPaginatedOfferResponse(
            // @ts-ignore
            paginatedOffersResponse.body,
            paginatedOffersResponse.pageCount,
            queryParams?.source,
          );
        }),
      );
  }

  getOffer(offerId: string): Observable<FormattedOfferData> {
    const singleOfferParams = this.buildLoadSingleOfferParams(offerId);
    return this.nydusNetworkDataService
      .get<OffersResponse>('offers', singleOfferParams)
      .pipe(
        tap((response) => {
          this.apiValidationService.validate<OffersResponse>({
            apiName: `offers?${decodeURIComponent(
              singleOfferParams.toString(),
            )}`,
            schema: offersResponseSchema,
            response,
          });
        }),
        map((offersResponse) => {
          return this.transformOfferResponse(offersResponse);
        }),
      );
  }

  transformOfferResponse(
    offersResponse: OffersResponse,
    source?: OfferSource,
  ): FormattedOfferData {
    const offers = offersResponse.data.map((offerResponse) => {
      return {
        id: offerResponse.id,
        ...offerResponse.attributes,
        ...(source ? { source } : {}),
      };
    });
    const offerMap: Record<string, Offer> = {};
    const count = offersResponse.meta.count;
    const offerIds: string[] = [];

    offers.forEach((offer) => {
      offerMap[offer.id] = offer;
      offerIds.push(offer.id);
    });

    return {
      offerMap,
      offerIds,
      count,
    };
  }

  transformPaginatedOfferResponse(
    offersResponse: OffersResponse,
    totalPageCount: number,
    source?: OfferSource,
  ): FormattedPaginatedOfferData {
    return {
      ...this.transformOfferResponse(offersResponse, source),
      totalPageCount,
    };
  }

  buildParams(queryParams?: OfferQueryParams): HttpParams {
    let params = new HttpParams();

    if (!queryParams) {
      return params;
    }

    const { channel, category, city, sort, searchByBrand, source } =
      queryParams;
    if (channel) {
      params = params.set('filter[channels][$eq]', channel);
    }
    if (category) {
      params = params.set('filter[categories][$eq]', category);
    }
    if (city) {
      params = params.set('filter[cities][$eq]', city);
    }
    if (sort) {
      if (sort === '-brand' || sort === 'brand') {
        params = params.set('sort', sort);
      } else {
        params = params.set('sort', `${sort},brand`);
      }
    }
    if (searchByBrand) {
      params = params.set('search[brand]', searchByBrand);
    }
    if (source) {
      params = params.set('source', source);
    }
    return params;
  }

  private buildLoadSingleOfferParams(offerId: string): HttpParams {
    const params = new HttpParams().set('filter[id][$eq]', offerId);

    return params;
  }
}
